import clsx from 'clsx';
import React from 'react';

import { PrimaryButton } from '@components/Button/variants';
import TIPollsAppIcon from '@components/Images/Rebranding/TIPollsAppIcon';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';

import { DevicePlatform, GOOGLE_PLAY_URL, POLLS_APP_STORE_URL } from '../constants';
import styles from './styles.module.scss';

const CAPTION_CLASSES =
  'm-0 font-suisse-intl text-[15px] font-normal leading-none tracking-wider text-[#a7a3a8]';

export const CTA_BY_PLATFORM: Record<
  DevicePlatform,
  { caption: string; href: string; store: string }
> = {
  android: { caption: 'On Android', href: GOOGLE_PLAY_URL, store: 'Google Play' },
  desktop: {
    caption: 'Available on Apple App Store and Google Play',
    href: POLLS_APP_STORE_URL,
    store: 'the App Store',
  },
  ios: { caption: 'On iPhone', href: POLLS_APP_STORE_URL, store: 'the App Store' },
};

interface DownloadCtaProps {
  platform: DevicePlatform;
  qrUrl: string;
}

const DownloadCta = ({ platform, qrUrl }: DownloadCtaProps) => {
  const { caption, href, store } = CTA_BY_PLATFORM[platform];
  const { trackEvent } = useEventTracking();

  return (
    <section
      aria-labelledby="polls-download-cta"
      className={clsx(styles.section, 'py-56 sm:py-72 md:py-80 xl:py-96')}
    >
      <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center px-20 text-center sm:px-40 lg:px-64 xl:px-80">
        <TIPollsAppIcon className="h-80 w-80 shrink-0 sm:h-96 sm:w-96" aria-hidden="true" />
        <h2
          className="m-0 mt-24 font-suisse-intl text-[38px] font-bold leading-[1.05] tracking-tighter text-white sm:text-[48px] md:text-[52px] xl:text-[60px]"
          id="polls-download-cta"
        >
          Five questions.
          <br />
          <span className="block text-primary-magenta">Cast your first vote today.</span>
        </h2>
        <p className="m-0 mt-28 max-w-[640px] font-suisse-intl text-[17px] font-normal leading-[1.4] text-[#dad6d9] sm:text-[18px] xl:text-[20px]">
          Download The Information Polls on {store}. Polls are free with your work email.
        </p>
        {platform !== 'desktop' ? (
          <>
            <PrimaryButton
              onClickCapture={() =>
                trackEvent(
                  click({
                    clickTarget: ClickTarget.PollsAppDownloadCta,
                    clickText: 'Download app free',
                    clickLink: href,
                  })
                )
              }
              as="externalLink"
              href={href}
              size="small"
              className="!mt-24 !w-fit !rounded-full !px-32 !py-16 !text-[18px] !text-white !no-underline hover:!border-primary-magenta hover:!bg-primary-magenta hover:!text-white focus:!border-primary-magenta focus:!bg-primary-magenta focus:!text-white"
            >
              Download app free
            </PrimaryButton>
            <p className={clsx(CAPTION_CLASSES, 'mt-20')}>{caption}</p>
          </>
        ) : (
          qrUrl && (
            <div className="mt-32 flex flex-col items-center">
              <img
                alt="Polls app QR code"
                className="block h-[128px] w-[128px] rounded-8 bg-white"
                height={128}
                src={qrUrl}
                width={128}
              />
              <p className={clsx(CAPTION_CLASSES, 'mt-16')}>{caption}</p>
            </div>
          )
        )}
      </div>
    </section>
  );
};

export default DownloadCta;
